<script setup lang="ts">
import { useI18n } from '../../i18n';
import LoadingSpin from '../Shared/LoadingSpin.vue';

const { t } = useI18n();

const props = defineProps({
  data: {
    type: Object,
    required: false,
    default: () => null,
  },
});
</script>

<template>
  <div class="w-full text-center text-xs truncate overflow-hidden whitespace-nowrap">
    <div v-if="props.data">
      <!-- Statut Rejeté -->
      <div
        v-if="props.data.state?.status === 'REJETEE'"
        class="inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-red-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'triangle-exclamation']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("rejected") }}</span>
      </div>

      <!-- Statut Refusé Chorus -->
      <div
        v-else-if="props.data.state?.status === 'REFUSEE_1'"
        class="inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-red-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'times-circle']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("chorus_refused") }}</span>
      </div>

      <!-- Statut Accepté -->
      <div
        v-else-if="props.data.state?.status === 'ACCEPTED'"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-emerald-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'check']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("sent") }}</span>
      </div>

      <!-- À compléter -->
      <div
        v-else-if="props.data.invalidData.length"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-orange-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'edit']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("to_complete") }}</span>
      </div>

      <!-- À valider -->
      <div
        v-else-if="!props.data.verifiedAt"
        class="group inline-flex items-center gap-1.5 text-slate-700 rounded-full py-1.5 px-3 bg-yellow-400 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'clock']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("to_valid") }}</span>
      </div>

      <!-- Erreur -->
      <div
        v-else-if="props.data.state?.status === 'ERROR'"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-slate-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'exclamation-triangle']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200 text-yellow-300"
        />
        <span class="tracking-wide">Erreur statut</span>
      </div>

      <!-- À envoyer -->
      <div
        v-else-if="props.data.state === null"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-blue-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'paper-plane']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("to_send") }}</span>
      </div>

      <!-- Mis en paiement -->
      <div
        v-else-if="props.data.state?.status === 'MISE_EN_PAIEMENT'"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-green-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'credit-card']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("chorus_payment") }}</span>
      </div>

      <!-- Mis à disposition -->
      <div
        v-else-if="props.data.state?.status === 'MISE_A_DISPOSITION'"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-green-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'download']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide">{{ t("chorus_disposal") }}</span>
      </div>

      <!-- Autres statuts -->
      <div
        v-else-if="props.data.state?.status"
        class="group inline-flex items-center gap-1.5 text-white rounded-full py-1.5 px-3 bg-emerald-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
      >
        <fa
          :icon="['fas', 'info-circle']"
          class="w-3 h-3 group-hover:scale-110 transition-transform duration-200"
        />
        <span class="tracking-wide capitalize">
          {{ props.data.state?.status.split("_").join(" ").toLowerCase() }}
        </span>
      </div>

      <!-- Loading état inconnu -->
      <div
        v-else
        class="inline-flex items-center gap-2 text-slate-600 rounded-full py-1.5 px-3 bg-slate-200 shadow-lg font-medium"
      >
        <LoadingSpin class="w-3 h-3" />
        <span class="tracking-wide text-xs">Chargement...</span>
      </div>
    </div>

    <!-- Loading donnée manquante -->
    <div
      v-else
      class="inline-flex items-center gap-2 text-slate-600 rounded-full py-1.5 px-3 bg-slate-200 shadow-lg font-medium"
    >
      <LoadingSpin class="w-3 h-3" />
      <span class="tracking-wide text-xs">Chargement...</span>
    </div>
  </div>
</template>
